import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Crown, Trophy, History, MapPin, Flame, Users, Radio, Search, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import ArenaxRivalryBadgeStrip from './ArenaxRivalryBadgeStrip';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Phase 2J — Eternal Leagues discovery.
 *
 * Hyperlocal sports memory surface. Each season card carries:
 *  • Reigning Champions
 *  • Dynasty Watch (most-titled squad)
 *  • Current Split CTA (live link to in-progress / open registration tournament)
 *  • Splits depth + active squads count
 *  • Live matches counter when any split is in_progress
 *
 * Tone: persistent, tribal, narrative-first. Never bookings-first.
 */
export default function ArenaxSeasons() {
  const { user } = useAuth();
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState('');
  const [sport, setSport] = useState('');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (city) params.set('city', city);
    if (sport) params.set('sport', sport);
    api.get(`/arenax/seasons${params.toString() ? '?' + params.toString() : ''}`)
      .then(r => setSeasons(r.data?.seasons || []))
      .catch(() => setSeasons([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [city, sport]);

  const filteredSeasons = useMemo(() => {
    if (!search) return seasons;
    const q = search.toLowerCase();
    return seasons.filter(s =>
      (s.name || '').toLowerCase().includes(q) ||
      (s.locality || '').toLowerCase().includes(q) ||
      (s.reigning_champion_squad_name || '').toLowerCase().includes(q)
    );
  }, [seasons, search]);

  const cities = useMemo(() =>
    Array.from(new Set(seasons.map(s => s.city).filter(Boolean))).slice(0, 8),
    [seasons]);
  const sports = useMemo(() =>
    Array.from(new Set(seasons.map(s => s.sport).filter(Boolean))).slice(0, 8),
    [seasons]);

  // Aggregate dynasty across all seasons
  const dynastyWatch = useMemo(() => {
    const candidates = seasons
      .filter(s => s.dynasty_leader_titles >= 2)
      .sort((a, b) => b.dynasty_leader_titles - a.dynasty_leader_titles)
      .slice(0, 1);
    return candidates[0] || null;
  }, [seasons]);

  const liveTotal = useMemo(() =>
    seasons.reduce((sum, s) => sum + (s.live_matches_count || 0), 0),
    [seasons]);

  return (
    <ArenaxLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 lg:py-12" data-testid="arenax-seasons-page">
        {/* Hero */}
        <div className="rounded-2xl border border-amber-500/15 p-6 lg:p-8 mb-6"
          style={{
            background: 'linear-gradient(160deg, rgba(245,158,11,0.07), rgba(244,63,94,0.05) 50%, var(--ax-card-bg-soft))',
          }}>
          <div className="flex items-center gap-2 mb-3">
            <History className="h-4 w-4 text-amber-300" />
            <span className="text-[10px] tracking-[0.3em] text-amber-300 font-bold uppercase">
              Eternal Leagues
            </span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black text-slate-100 leading-tight mb-3"
            data-testid="arenax-seasons-headline">
            <span className="arenax-hero-gradient-text">
              Your city's sports memory
            </span>
          </h1>
          <p className="text-sm sm:text-base text-slate-300 max-w-2xl leading-relaxed">
            Recurring local leagues. Reigning champions. Dynasties forming season after season.
            This is where weekly rituals turn into history.
          </p>

          {/* Pulse strip */}
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
            <span className="inline-flex items-center gap-1.5 text-slate-300">
              <Trophy className="h-3.5 w-3.5 text-amber-300" />
              <span className="tabular-nums font-bold text-slate-100">{seasons.length}</span> leagues
            </span>
            {liveTotal > 0 && (
              <span className="inline-flex items-center gap-1.5 text-amber-200" data-testid="arenax-seasons-live-pill">
                <Radio className="h-3.5 w-3.5 animate-pulse" />
                <span className="tabular-nums font-bold">{liveTotal}</span> live now
              </span>
            )}
            {dynastyWatch && (
              <span className="inline-flex items-center gap-1.5 text-emerald-200" data-testid="arenax-dynasty-watch-pill">
                <Crown className="h-3.5 w-3.5" />
                Dynasty: <span className="font-bold text-slate-100">{dynastyWatch.dynasty_leader_squad_name}</span>
                <span className="text-slate-400">· {dynastyWatch.dynasty_leader_titles}× in {dynastyWatch.name}</span>
              </span>
            )}
          </div>
        </div>

        {/* Filters + Create */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
            <input
              type="text"
              placeholder="Search by league, locality or champion…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="arenax-seasons-search"
              className="w-full pl-9 pr-3 py-2 rounded-lg text-xs text-slate-200 bg-slate-900/60 border border-slate-800 focus:outline-none focus:border-cyan-500/40 placeholder:text-slate-600"
            />
          </div>
          {cities.length > 0 && (
            <div className="flex items-center gap-1.5" data-testid="arenax-seasons-city-filters">
              <button onClick={() => setCity('')}
                className={`px-2.5 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${
                  !city ? 'text-amber-200 bg-amber-500/15 border border-amber-500/30' : 'text-slate-500 border border-slate-800 hover:text-slate-300'
                }`}>
                All cities
              </button>
              {cities.map(c => (
                <button key={c} onClick={() => setCity(c === city ? '' : c)}
                  className={`px-2.5 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${
                    city === c ? 'text-cyan-200 bg-cyan-500/15 border border-cyan-500/30' : 'text-slate-500 border border-slate-800 hover:text-slate-300'
                  }`}>
                  {c}
                </button>
              ))}
            </div>
          )}
          {sports.length > 0 && (
            <div className="flex items-center gap-1.5" data-testid="arenax-seasons-sport-filters">
              {sports.map(s => (
                <button key={s} onClick={() => setSport(s === sport ? '' : s)}
                  className={`px-2.5 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${
                    sport === s ? 'text-pink-200 bg-pink-500/15 border border-pink-500/30' : 'text-slate-500 border border-slate-800 hover:text-slate-300'
                  }`}>
                  <Flame className="h-3 w-3 inline -mt-0.5 mr-1" />
                  {s}
                </button>
              ))}
            </div>
          )}
          {user && (
            <button
              onClick={() => setCreating(true)}
              data-testid="arenax-seasons-create-btn"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-slate-950 bg-gradient-to-r from-amber-300 to-pink-400 hover:brightness-110 transition-all">
              <Plus className="h-3.5 w-3.5" /> Start a league
            </button>
          )}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="arenax-seasons-loading">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-56 rounded-2xl border border-slate-800 animate-pulse" />
            ))}
          </div>
        ) : filteredSeasons.length === 0 ? (
          <EmptyState onCreate={() => setCreating(true)} canCreate={!!user} />
        ) : (
          <>
            {/* Phase 2K PR 2 — City-wide rivalry badge strip */}
            <div className="mb-5">
              <ArenaxRivalryBadgeStrip mode="city"
                city={city || undefined} sport={sport || undefined}
                title="Rivalries to watch" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="arenax-seasons-grid">
              {filteredSeasons.map(s => (
                <SeasonCard key={s.id} season={s} />
              ))}
            </div>
          </>
        )}
      </div>

      {creating && <CreateSeasonModal onClose={() => setCreating(false)} onCreated={(s) => {
        setCreating(false);
        load();
      }} />}
    </ArenaxLayout>
  );
}

function SeasonCard({ season }) {
  const isLive = (season.live_matches_count || 0) > 0;
  return (
    <Link
      to={`/arenax/seasons/${season.id}`}
      data-testid={`arenax-season-card-${season.id}`}
      className="group block rounded-2xl border border-slate-800 hover:border-amber-500/40 p-5 transition-all duration-300 hover:translate-y-[-2px]"
      style={{
        background: isLive
          ? 'linear-gradient(160deg, rgba(245,158,11,0.08), rgba(244,63,94,0.05) 60%, var(--ax-card-bg-soft))'
          : 'linear-gradient(160deg, rgba(8,145,178,0.05), var(--ax-card-bg-soft))',
      }}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[9px] tracking-[0.25em] text-amber-300 font-bold uppercase">
              Eternal League
            </span>
            {isLive && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider text-amber-100 bg-amber-500/30 border border-amber-400/50"
                data-testid={`arenax-season-card-live-${season.id}`}>
                <Radio className="h-2.5 w-2.5 animate-pulse" />
                {season.live_matches_count} live
              </span>
            )}
          </div>
          <h3 className="text-xl font-black text-slate-100 leading-tight group-hover:text-amber-200 transition-colors truncate">
            {season.name}
          </h3>
          <div className="flex items-center gap-3 text-[10px] text-slate-400 mt-1">
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {season.locality ? `${season.locality}, ` : ''}{season.city}
            </span>
            <span className="inline-flex items-center gap-1">
              <Flame className="h-3 w-3" /> {season.sport}
            </span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-black text-amber-300 tabular-nums leading-none">{season.splits_count || 0}</div>
          <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mt-1">splits</div>
        </div>
      </div>

      {/* Reigning champion */}
      {season.reigning_champion_squad_name && (
        <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20"
          data-testid={`arenax-season-card-reigning-${season.id}`}>
          <Crown className="h-4 w-4 text-amber-300 shrink-0" />
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-wider text-amber-300/70 font-bold">Current Champions</div>
            <div className="text-sm font-bold text-slate-100 truncate">{season.reigning_champion_squad_name}</div>
          </div>
        </div>
      )}

      {/* Dynasty leader (only if 2+ titles AND different from reigning) */}
      {season.dynasty_leader_titles >= 2 && season.dynasty_leader_squad_id !== season.reigning_champion_squad_id && (
        <div className="mt-2 flex items-center gap-2 p-3 rounded-lg bg-emerald-500/8 border border-emerald-500/20"
          data-testid={`arenax-season-card-dynasty-${season.id}`}>
          <Trophy className="h-4 w-4 text-emerald-300 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[9px] uppercase tracking-wider text-emerald-300/70 font-bold">Most Titles</div>
            <div className="text-sm font-bold text-slate-100 truncate">
              {season.dynasty_leader_squad_name}
              <span className="text-emerald-300 ml-1.5 tabular-nums">{season.dynasty_leader_titles}×</span>
            </div>
          </div>
        </div>
      )}

      {/* Active split + active squads strip */}
      <div className="mt-3 flex items-center justify-between gap-2 text-[10px]">
        {season.active_split_name ? (
          <span className="inline-flex items-center gap-1.5 text-cyan-200 font-bold"
            data-testid={`arenax-season-card-active-${season.id}`}>
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-300 animate-pulse" />
            Current split: <span className="text-slate-100 truncate">{season.active_split_name}</span>
          </span>
        ) : (
          <span className="text-slate-500">Awaiting next split</span>
        )}
        {season.active_squads_count > 0 && (
          <span className="inline-flex items-center gap-1 text-slate-400 shrink-0">
            <Users className="h-3 w-3" />
            <span className="tabular-nums">{season.active_squads_count}</span> squads
          </span>
        )}
      </div>
    </Link>
  );
}

function EmptyState({ onCreate, canCreate }) {
  return (
    <div className="text-center py-12 px-6 rounded-2xl border border-dashed border-slate-700/60 bg-slate-900/30"
      data-testid="arenax-seasons-empty">
      <History className="mx-auto h-8 w-8 text-slate-500 mb-3" />
      <h3 className="text-lg font-black text-slate-100 mb-1">No leagues here yet.</h3>
      <p className="text-sm text-slate-400 max-w-md mx-auto mb-4">
        The first recurring local league becomes the city's reference point. Be the one who starts it.
      </p>
      {canCreate && (
        <button onClick={onCreate}
          data-testid="arenax-seasons-empty-create-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-slate-950 bg-gradient-to-r from-amber-300 to-pink-400 hover:brightness-110 transition-all">
          <Plus className="h-3.5 w-3.5" /> Start the first league
        </button>
      )}
    </div>
  );
}

function CreateSeasonModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [sport, setSport] = useState('Football');
  const [city, setCity] = useState('Bengaluru');
  const [locality, setLocality] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name || !sport || !city) {
      toast.error('Name, sport and city are required');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post('/arenax/seasons', {
        name, sport, city,
        locality: locality || null,
        description: description || null,
      });
      toast.success('League created — claim its first split next.');
      onCreated(r.data?.season);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create league');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="arenax-season-create-modal" onClick={onClose}>
      <div className="rounded-2xl border border-amber-500/20 p-6 w-full max-w-md bg-slate-950"
        onClick={e => e.stopPropagation()}
        style={{ background: 'linear-gradient(160deg, rgba(245,158,11,0.06), var(--ax-card-bg-soft))' }}>
        <div className="flex items-center gap-2 mb-3">
          <History className="h-4 w-4 text-amber-300" />
          <span className="text-[10px] tracking-[0.3em] text-amber-300 font-bold uppercase">Start a league</span>
        </div>
        <h2 className="text-lg font-black text-slate-100 mb-4">Eternal League — name your tradition</h2>
        <div className="space-y-3">
          <input value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. HSR Saturday League"
            data-testid="arenax-season-create-name"
            className="w-full px-3 py-2 rounded-lg text-sm bg-slate-900 border border-slate-700 text-slate-100 focus:outline-none focus:border-amber-500/40" />
          <div className="grid grid-cols-2 gap-2">
            <input value={sport} onChange={e => setSport(e.target.value)}
              placeholder="Sport"
              data-testid="arenax-season-create-sport"
              className="w-full px-3 py-2 rounded-lg text-sm bg-slate-900 border border-slate-700 text-slate-100 focus:outline-none focus:border-amber-500/40" />
            <input value={city} onChange={e => setCity(e.target.value)}
              placeholder="City"
              data-testid="arenax-season-create-city"
              className="w-full px-3 py-2 rounded-lg text-sm bg-slate-900 border border-slate-700 text-slate-100 focus:outline-none focus:border-amber-500/40" />
          </div>
          <input value={locality} onChange={e => setLocality(e.target.value)}
            placeholder="Locality (e.g. HSR Layout) — optional"
            data-testid="arenax-season-create-locality"
            className="w-full px-3 py-2 rounded-lg text-sm bg-slate-900 border border-slate-700 text-slate-100 focus:outline-none focus:border-amber-500/40" />
          <textarea value={description} onChange={e => setDescription(e.target.value)}
            placeholder="One line about the rhythm of this league…"
            data-testid="arenax-season-create-description"
            rows={2}
            className="w-full px-3 py-2 rounded-lg text-sm bg-slate-900 border border-slate-700 text-slate-100 focus:outline-none focus:border-amber-500/40 resize-none" />
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md text-xs text-slate-400 hover:text-slate-200">Cancel</button>
          <button onClick={submit} disabled={saving}
            data-testid="arenax-season-create-submit"
            className="px-4 py-1.5 rounded-md text-xs font-bold text-slate-950 bg-gradient-to-r from-amber-300 to-pink-400 hover:brightness-110 disabled:opacity-50">
            {saving ? 'Creating…' : 'Create league'}
          </button>
        </div>
      </div>
    </div>
  );
}
